
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Clock, AlertTriangle, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface PenaltiesLogProps {
  providerId: string;
}

const PenaltiesLog = ({ providerId }: PenaltiesLogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [waivingPenaltyId, setWaivingPenaltyId] = useState<string | null>(null);

  const { data: penalties, isLoading } = useQuery({
    queryKey: ['penalties-log', providerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('penalties_log')
        .select(`
          *,
          bookings (
            booking_number,
            customer_name,
            customer_email
          )
        `)
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
    enabled: !!providerId,
  });

  const waivePenaltyMutation = useMutation({
    mutationFn: async (penaltyId: string) => {
      const { error } = await supabase.rpc('waive_penalty', {
        p_penalty_id: penaltyId
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Penalty Waived",
        description: "The penalty has been successfully waived and removed from the customer's balance.",
      });
      queryClient.invalidateQueries({ queryKey: ['penalties-log', providerId] });
      setWaivingPenaltyId(null);
    },
    onError: (error) => {
      console.error('Error waiving penalty:', error);
      toast({
        title: "Error",
        description: "Failed to waive penalty. Please try again.",
        variant: "destructive",
      });
      setWaivingPenaltyId(null);
    },
  });

  const handleWaivePenalty = (penaltyId: string) => {
    setWaivingPenaltyId(penaltyId);
    waivePenaltyMutation.mutate(penaltyId);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Penalties Log</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5" />
          <span>Penalties Log</span>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {!penalties || penalties.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No penalties recorded yet</p>
            <p className="text-sm">Penalties will appear here when customers overstay their bookings</p>
          </div>
        ) : (
          <div className="space-y-4">
            {penalties.map((penalty) => (
              <div key={penalty.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <Badge variant="destructive">
                        {penalty.penalty_type.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        Booking #{penalty.bookings?.booking_number}
                      </span>
                    </div>
                    <p className="font-medium">{penalty.bookings?.customer_name}</p>
                    <p className="text-sm text-gray-600">{penalty.bookings?.customer_email}</p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="text-right space-y-1">
                      <p className="font-semibold text-lg">
                        £{Number(penalty.penalty_amount).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(penalty.applied_at), { addSuffix: true })}
                      </p>
                    </div>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={waivingPenaltyId === penalty.id || penalty.reason?.includes('WAIVED')}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Waive Penalty</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to waive this £{Number(penalty.penalty_amount).toFixed(2)} penalty? 
                            This will remove the penalty from the customer's balance and cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleWaivePenalty(penalty.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Waive Penalty
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                
                {penalty.overstay_minutes && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>Overstayed by {penalty.overstay_minutes} minutes</span>
                  </div>
                )}
                
                {penalty.reason && (
                  <p className={`text-sm p-2 rounded ${
                    penalty.reason.includes('WAIVED') 
                      ? 'text-green-700 bg-green-50 border border-green-200' 
                      : 'text-gray-600 bg-gray-50'
                  }`}>
                    {penalty.reason}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PenaltiesLog;
